import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { useTickets } from '../hooks/useTickets';
import Badge from '../components/Badge';
import SLAIndicator from '../components/SLAIndicator';
import Button from '../components/Button';
import { Card, DataTable, Select } from '../components/ui';
import type { Column } from '../components/ui';
import { fmt, requesterStatusLabel } from '../utils/helpers';
import { STATUS_LABELS } from '../utils/constants';
import type { Ticket } from '../types';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  ...['PendingLineManager','PendingITManager','PendingTopManagement','PendingRequester','Assigned',
      'InProgress','Deferred','Resolved','ClosedCompleted','ClosedCannotImplement','ClosedOutOfScope',
      'ClosedDuplicate','Rejected','Closed']
    .map(v => ({ value: v, label: STATUS_LABELS[v] ?? v })),
];
const PRIORITY_OPTIONS = ['', 'Critical', 'High', 'Medium', 'Low'].map(p => ({ value: p, label: p || 'All Priorities' }));
const PRIORITY_RANK: Record<string, number> = { Critical: 4, High: 3, Medium: 2, Low: 1 };

function priorityClass(p: string) {
  return p === 'Critical' ? 'text-red-700' : p === 'High' ? 'text-orange-600'
    : p === 'Medium' ? 'text-yellow-600' : 'text-green-700';
}

export default function Tickets() {
  const navigate = useNavigate();
  const location = useLocation();
  // Dashboard stat cards deep-link here with a status to pre-filter to
  // (e.g. navigate('/tickets', { state: { status: 'InProgress' } })).
  const [status, setStatus]     = useState((location.state as { status?: string } | null)?.status ?? '');
  const [priority, setPriority] = useState('');
  const [search, setSearch]     = useState('');
  const { tickets, loading }    = useTickets({ status, priority, search });

  const columns: Column<Ticket>[] = [
    { key: 'id', header: 'Reference', sortValue: t => t.id,
      render: t => <span className="font-mono text-xs text-brand-dark">{t.id}</span> },
    { key: 'category', header: 'Category', sortValue: t => t.items?.[0]?.category ?? '',
      render: t => t.items?.[0]?.category || '—' },
    { key: 'priority', header: 'Priority', sortValue: t => PRIORITY_RANK[t.priority] ?? 0,
      render: t => <span className={`text-xs font-medium ${priorityClass(t.priority)}`}>{t.priority}</span> },
    { key: 'status', header: 'Status', sortValue: t => t.status,
      render: t => <Badge status={t.status} small label={requesterStatusLabel(t, true) ?? undefined} /> },
    { key: 'sla', header: 'SLA', render: t => <SLAIndicator ticket={t} compact /> },
    { key: 'submitted', header: 'Submitted', sortValue: t => new Date(t.submittedAt ?? 0).getTime(),
      render: t => <span className="text-xs text-gray-500 whitespace-nowrap">{fmt(t.submittedAt)}</span> },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-navy">My Tickets</h1>
        <Button size="sm" onClick={() => navigate('/tickets/new')}><Plus size={15} /> New Request</Button>
      </div>

      <Card flush>
        <div className="flex flex-wrap items-end gap-2 p-3 border-b border-gray-100">
          <div className="relative flex-1 min-w-[160px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tickets…"
              className="w-full text-sm rounded-lg border border-gray-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand" />
          </div>
          <Select options={STATUS_OPTIONS} value={status} onChange={e => setStatus(e.target.value)} className="w-44" />
          <Select options={PRIORITY_OPTIONS} value={priority} onChange={e => setPriority(e.target.value)} className="w-40" />
        </div>
        <DataTable
          columns={columns}
          data={tickets}
          rowKey={t => t.id}
          loading={loading}
          onRowClick={t => navigate(`/tickets/${t.id}`, { state: { activeNav: '/tickets' } })}
          pageSize={20}
          emptyTitle="No tickets found"
          emptyDescription="Try adjusting your filters, or submit a new request."
          renderMobileCard={t => (
            <div className="px-1">
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="font-mono text-xs font-semibold text-brand-dark break-all">{t.id}</span>
                <Badge status={t.status} small label={requesterStatusLabel(t, true) ?? undefined} />
              </div>
              <p className="text-xs text-navy mb-1.5">{t.items?.[0]?.category || '—'}</p>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${priorityClass(t.priority)}`}>{t.priority}</span>
                <SLAIndicator ticket={t} compact />
              </div>
            </div>
          )}
        />
      </Card>
    </div>
  );
}
